<template>
  <div
    class="min-h-[70vh] bg-gradient-to-br from-slate-50 via-gray-50 to-blue-50/30 pt-[90px] pb-[100px]"
  >
    <!-- Pattern décoratif en arrière-plan -->
    <div class="absolute inset-0 overflow-hidden pointer-events-none">
      <div
        class="absolute top-20 left-10 w-32 h-32 bg-gradient-to-br from-emerald-200/20 to-teal-200/20 rounded-full blur-xl"
      ></div>
      <div
        class="absolute hidden md:block right-35 w-90 h-48"
        :class="isAuthenticated ? 'top-30' : 'top-12'"
      >
        <img src="@/assets/img/corner-image.webp" alt="" />
      </div>
      <div
        class="absolute bottom-20 left-1/3 w-40 h-40 bg-gradient-to-br from-purple-200/20 to-pink-200/20 rounded-full blur-xl"
      ></div>
    </div>

    <div class="relative container mx-auto px-4">
      <!-- Navigation améliorée avec breadcrumb -->
      <div class="mb-10">
        <nav class="mb-6" v-if="currentStep > 1">
          <!-- Version mobile (écrans < 768px) -->
          <div class="block md:hidden">
            <div class="flex items-center justify-between">
              <!-- Bouton retour -->
              <button
                @click="goBack"
                class="group flex cursor-pointer items-center justify-center w-10 h-10 bg-gradient-to-r from-primary-400 to-primary-600 hover:from-primary-300 hover:to-primary-500 text-white rounded-lg transition-all duration-300 transform hover:scale-105"
              >
                <i
                  class="fa-solid fa-arrow-left text-sm group-hover:-translate-x-0.5 transition-transform"
                ></i>
              </button>

              <!-- Indicateur d'étapes compact -->
              <div class="flex items-center space-x-1 text-xs">
                <span class="text-gray-500">Étape</span>
                <span class="font-semibold text-primary-600">{{ currentStep }}</span>
                <span class="text-gray-400">/</span>
                <span class="text-gray-500">4</span>
              </div>
            </div>

            <!-- Breadcrumb mobile simplifié -->
            <div class="mt-3 flex flex-col space-y-2">
              <div v-if="currentStep > 1" class="flex items-center space-x-2 text-sm">
                <div class="w-1.5 h-1.5 bg-emerald-400 rounded-full flex-shrink-0"></div>
                <span class="text-gray-600 font-medium truncate">{{ selectedExamType?.name }}</span>
              </div>

              <div v-if="currentStep > 2" class="flex items-center space-x-2 text-sm ml-4">
                <div class="w-1.5 h-1.5 bg-blue-400 rounded-full flex-shrink-0"></div>
                <span class="text-gray-600 font-medium truncate">{{ selectedExam?.name }}</span>
              </div>
            </div>
          </div>

          <!-- Version desktop (écrans ≥ 768px) -->
          <div class="hidden md:flex items-center space-x-3 text-sm">
            <!-- Bouton retour stylisé -->
            <button
              @click="goBack"
              class="group flex cursor-pointer items-center justify-center w-11 h-11 bg-gradient-to-r from-primary-400 to-primary-600 hover:from-primary-300 hover:to-primary-500 text-white rounded-xl transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
            >
              <i
                class="fa-solid fa-arrow-left text-sm group-hover:-translate-x-0.5 transition-transform"
              ></i>
            </button>

            <!-- Breadcrumb desktop -->
            <div class="flex items-center space-x-3">
              <button
                v-if="currentStep > 1"
                @click="goBack"
                class="flex items-center space-x-2 bg-white/80 backdrop-blur-sm hover:bg-white px-4 py-2 rounded-xl transition-all duration-200 hover:shadow-md border border-gray-200/50"
              >
                <div class="w-2 h-2 bg-emerald-400 rounded-full"></div>
                <span class="text-gray-700 hover:text-emerald-600 font-medium transition-colors">
                  {{ selectedExamType?.name }}
                </span>
              </button>

              <i v-if="currentStep > 2" class="fa-solid fa-chevron-right text-gray-300 text-xs"></i>

              <button
                v-if="currentStep > 2"
                @click="resetStepToTwo()"
                class="flex items-center space-x-2 bg-white/80 backdrop-blur-sm hover:bg-white px-4 py-2 rounded-xl transition-all duration-200 hover:shadow-md border border-gray-200/50"
              >
                <div class="w-2 h-2 bg-blue-400 rounded-full"></div>
                <span class="text-gray-700 hover:text-blue-600 font-medium transition-colors">
                  {{ selectedExam?.name }}
                </span>
              </button>
            </div>
          </div>
        </nav>

        <!-- En-tête avec design moderne -->
        <div class=" ">
          <div class="mb-4">
            <h1
              class="text-2xl md:text-3xl font-bold bg-gradient-to-r from-gray-900 via-gray-700 to-gray-900 bg-clip-text text-transparent leading-tight"
            >
              {{ getTitle() }}
            </h1>
            <!-- Ligne décorative -->
            <div
              class="w-20 h-1 bg-gradient-to-r from-primary-400 to-primary-500 mt-4 rounded-full"
            ></div>
          </div>

          <p class="text-lg text-gray-600 leading-relaxed">
            {{ getSubtitle() }}
          </p>
        </div>
      </div>

      <!-- États de chargement et d'erreur -->
      <LoadingState v-if="isLoading" />
      <ErrorState v-else-if="error" :message="error" @retry="retry" />

      <!-- Grille des types d'examen -->
      <div class="grid gap-8 md:grid-cols-2 mx-auto" v-else-if="currentStep === 1">
        <RouterLink
          :to="'/dashboard/exercises/' + examType.slug"
          v-for="examType in examTypes"
          :key="examType.slug"
        >
          <ExamTypeCard :examType="examType" @select="selectExamType" />
        </RouterLink>
      </div>

      <!-- Grille des examens -->
      <div class="grid gap-6 md:grid-cols-2 lg:grid-cols-2 mx-auto" v-else-if="currentStep === 2">
        <RouterLink
          v-for="exam in exams"
          :key="exam.slug"
          :to="'/dashboard/exercises/' + selectedExamType.slug + '/' + exam.slug"
        >
          <ExamCard :exam="exam" @select="selectExam" />
        </RouterLink>
      </div>

      <!-- Sélection du mode d'entraînement redesigné -->
      <div v-else-if="currentStep === 3" class="mx-auto">
        <div class="grid gap-8 md:grid-cols-2">
          <div id="entrainement"
            @click="selectPracticeType('taining')"
            class="relative bg-gradient-to-br from-white to-indigo-50/30 border border-gray-200 rounded-2xl p-8 cursor-pointer transition-all duration-300 hover:border-indigo-300 hover:shadow-2xl hover:-translate-y-2 group overflow-hidden"
          >
            <div
              class="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-indigo-400 to-purple-500"
            ></div>

            <div
              class="absolute -top-4 -right-4 w-20 h-20 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full opacity-50 group-hover:opacity-70 transition-opacity"
            ></div>

            <div class="relative z-10">
              <div class="flex items-start justify-between mb-6">
                <div class="flex-1">
                  <div class="flex items-center space-x-3 mb-3">
                    <div
                      class="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300"
                    >
                      <i class="fa-solid fa-dumbbell text-white text-xl"></i>
                    </div>
                    <div>
                      <h3
                        class="text-2xl font-bold text-gray-900 group-hover:text-indigo-600 transition-colors"
                      >
                        Mode Entraînement
                      </h3>
                      <div
                        class="w-0 h-0.5 bg-gradient-to-r from-indigo-400 to-purple-500 group-hover:w-16 transition-all duration-500"
                      ></div>
                    </div>
                  </div>

                  <div
                    class="inline-flex items-center space-x-2 bg-gradient-to-r from-indigo-100 to-purple-100 px-4 py-2 rounded-xl"
                  >
                    <div class="w-2 h-2 bg-indigo-500 rounded-full animate-pulse"></div>
                    <span class="text-sm font-semibold text-indigo-700">Pratique libre</span>
                  </div>
                </div>

                <div
                  class="w-10 h-10 bg-gray-100 group-hover:bg-indigo-100 rounded-xl flex items-center justify-center transition-all duration-300 group-hover:scale-110"
                >
                  <i
                    class="fa-solid fa-arrow-right text-gray-400 group-hover:text-indigo-500 group-hover:translate-x-1 transition-all"
                  ></i>
                </div>
              </div>

              <p
                class="text-gray-600 leading-relaxed mb-6 group-hover:text-gray-700 transition-colors"
              >
                Entraînez-vous à votre rythme sans contrainte de temps. Idéal pour apprendre et
                comprendre les concepts. Vous pouvez réviser vos réponses et bénéficier
                d'explications détaillées pour progresser efficacement.
              </p>

              <div class="space-y-3">
                <div class="flex items-center space-x-3 text-sm">
                  <div
                    class="w-6 h-6 bg-gradient-to-br from-green-400 to-emerald-500 rounded-lg flex items-center justify-center"
                  >
                    <i class="fa-solid fa-clock text-white text-xs"></i>
                  </div>
                  <span
                    class="text-gray-600 group-hover:text-indigo-700 transition-colors font-medium"
                    >Temps illimité</span
                  >
                </div>
                <div
                  class="flex items-center space-x-3 text-sm"
                  v-if="['READING', 'LISTENING'].includes(selectedExam?.type)"
                >
                  <div
                    class="w-6 h-6 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-lg flex items-center justify-center"
                  >
                    <i class="fa-solid fa-lightbulb text-white text-xs"></i>
                  </div>
                  <span
                    class="text-gray-600 group-hover:text-indigo-700 transition-colors font-medium"
                    >Correction immédiate</span
                  >
                </div>
                <div
                  class="flex items-center space-x-3 text-sm"
                  v-if="['LISTENING'].includes(selectedExam?.type)"
                >
                  <div
                    class="w-6 h-6 bg-gradient-to-br from-purple-400 to-indigo-500 rounded-lg flex items-center justify-center"
                  >
                    <i class="fa-solid fa-headphones text-white text-xs"></i>
                  </div>
                  <span
                    class="text-gray-600 group-hover:text-indigo-700 transition-colors font-medium"
                    >Écoute d'audio illimitée</span
                  >
                </div>
                <div
                  class="flex items-center space-x-3 text-sm"
                  v-if="['WRITING', 'SPEAKING'].includes(selectedExam?.type)"
                >
                  <div
                    class="w-6 h-6 bg-gradient-to-br from-gray-400 to-gray-600 rounded-lg flex items-center justify-center"
                  >
                    <i class="fa-solid fa-ban text-white text-xs"></i>
                  </div>
                  <span
                    class="text-gray-600 group-hover:text-emerald-700 transition-colors font-medium"
                    >Sans correction</span
                  >
                </div>
              </div>
            </div>

            <div
              class="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000"
            ></div>
          </div>
          <RouterLink
            :to="'/dashboard/exercises/' + selectedExamType.slug + '/' + selectedExam.slug+'/exam'"
          >
            <div
            class="relative bg-gradient-to-br from-white to-emerald-50/30 border border-gray-200 rounded-2xl p-8 cursor-pointer transition-all duration-300 hover:border-emerald-300 hover:shadow-2xl hover:-translate-y-2 group overflow-hidden"
            @click="selectPracticeType('exam')"
          >
            <div
              class="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-400 to-teal-500"
            ></div>

            <div
              class="absolute -top-4 -right-4 w-20 h-20 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-full opacity-50 group-hover:opacity-70 transition-opacity"
            ></div>

            <div class="relative z-10">
              <div class="flex items-start justify-between mb-6">
                <div class="flex-1">
                  <div class="flex items-center space-x-3 mb-3">
                    <div
                      class="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300"
                    >
                      <i class="fa-solid fa-stopwatch text-white text-xl"></i>
                    </div>
                    <div>
                      <h3
                        class="text-2xl font-bold text-gray-900 group-hover:text-emerald-600 transition-colors"
                      >
                        Condition Réelle d'Examen
                      </h3>
                      <div
                        class="w-0 h-0.5 bg-gradient-to-r from-emerald-400 to-teal-500 group-hover:w-20 transition-all duration-500"
                      ></div>
                    </div>
                  </div>

                  <div
                    class="inline-flex items-center space-x-2 bg-gradient-to-r from-emerald-100 to-teal-100 px-4 py-2 rounded-xl"
                  >
                    <div class="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                    <span class="text-sm font-semibold text-emerald-700">Simulation réelle</span>
                  </div>
                </div>

                <div
                  class="w-10 h-10 bg-gray-100 group-hover:bg-emerald-100 rounded-xl flex items-center justify-center transition-all duration-300 group-hover:scale-110"
                >
                  <i
                    class="fa-solid fa-arrow-right text-gray-400 group-hover:text-emerald-500 group-hover:translate-x-1 transition-all"
                  ></i>
                </div>
              </div>

              <p
                class="text-gray-600 leading-relaxed mb-6 group-hover:text-gray-700 transition-colors"
              >
                Testez-vous dans les conditions exactes de l'examen avec un temps limité. Parfait
                pour évaluer votre niveau et vous habituer au stress de l'examen réel.
              </p>

              <div class="space-y-3">
                <div class="flex items-center space-x-3 text-sm">
                  <div
                    class="w-6 h-6 bg-gradient-to-br from-red-400 to-orange-500 rounded-lg flex items-center justify-center"
                  >
                    <i class="fa-solid fa-hourglass-half text-white text-xs"></i>
                  </div>
                  <span
                    class="text-gray-600 group-hover:text-emerald-700 transition-colors font-medium"
                    >Temps chronométré</span
                  >
                </div>
                <div
                  class="flex items-center space-x-3 text-sm"
                  v-if="['LISTENING', 'READING'].includes(selectedExam?.type)"
                >
                  <div
                    class="w-6 h-6 bg-gradient-to-br from-gray-400 to-gray-600 rounded-lg flex items-center justify-center"
                  >
                    <i class="fa-solid fa-ban text-white text-xs"></i>
                  </div>
                  <span
                    class="text-gray-600 group-hover:text-emerald-700 transition-colors font-medium"
                    >Sans aide</span
                  >
                </div>
                <div
                  class="flex items-center space-x-3 text-sm"
                  v-if="['LISTENING'].includes(selectedExam?.type)"
                >
                  <div
                    class="w-6 h-6 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-lg flex items-center justify-center"
                  >
                    <i class="fa-solid fa-ear-listen text-white text-xs"></i>
                  </div>
                  <span
                    class="text-gray-600 group-hover:text-emerald-700 transition-colors font-medium"
                    >Écoute d'audio limitée</span
                  >
                </div>
                <div
                  class="flex items-center space-x-3 text-sm"
                  v-if="['WRITING', 'SPEAKING'].includes(selectedExam?.type)"
                >
                  <div
                    class="w-6 h-6 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-lg flex items-center justify-center"
                  >
                    <svg
                      height="24px"
                      width="24px"
                      version="1.1"
                      id="_x32_"
                      xmlns="http://www.w3.org/2000/svg"
                      xmlns:xlink="http://www.w3.org/1999/xlink"
                      viewBox="0 0 512 512"
                      xml:space="preserve"
                      fill="#ffffff"
                      stroke="#ffffff"
                    >
                      <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                      <g
                        id="SVGRepo_tracerCarrier"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      ></g>
                      <g id="SVGRepo_iconCarrier">
                        <g>
                          <path
                            class="st0"
                            d="M247.355,106.9C222.705,82.241,205.833,39.18,197.46,0c-8.386,39.188-25.24,82.258-49.899,106.917 c-24.65,24.642-67.724,41.514-106.896,49.904c39.188,8.373,82.254,25.235,106.904,49.895c24.65,24.65,41.522,67.72,49.908,106.9 c8.373-39.188,25.24-82.258,49.886-106.917c24.65-24.65,67.724-41.514,106.896-49.904 C315.08,148.422,272.014,131.551,247.355,106.9z"
                          ></path>
                          <path
                            class="st0"
                            d="M407.471,304.339c-14.714-14.721-24.81-40.46-29.812-63.864c-5.011,23.404-15.073,49.142-29.803,63.872 c-14.73,14.714-40.464,24.801-63.864,29.812c23.408,5.01,49.134,15.081,63.864,29.811c14.73,14.722,24.81,40.46,29.82,63.864 c5.001-23.413,15.081-49.142,29.802-63.872c14.722-14.722,40.46-24.802,63.856-29.82 C447.939,329.14,422.201,319.061,407.471,304.339z"
                          ></path>
                          <path
                            class="st0"
                            d="M146.352,354.702c-4.207,19.648-12.655,41.263-25.019,53.626c-12.362,12.354-33.968,20.82-53.613,25.027 c19.645,4.216,41.251,12.656,53.613,25.027c12.364,12.362,20.829,33.96,25.036,53.618c4.203-19.658,12.655-41.255,25.023-53.626 c12.354-12.362,33.964-20.82,53.605-25.035c-19.64-4.2-41.251-12.656-53.613-25.019 C159.024,395.966,150.555,374.351,146.352,354.702z"
                          ></path>
                        </g>
                      </g>
                    </svg>
                  </div>
                  <span
                    class="text-gray-600 group-hover:text-emerald-700 transition-colors font-medium"
                    >Correction avec IA</span
                  >
                </div>
              </div>
            </div>

            <div
              class="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000"
            ></div>
          </div>
          </RouterLink>



        </div>
      </div>

      <!-- Grille des exercices -->
      <div v-else-if="currentStep === 4" class="grid gap-6 md:grid-cols-2 lg:grid-cols-3 max-w-7xl">
        <ExerciceCard
          v-for="exercice in exercices"
          :key="exercice.slug"
          :exercice="{ ...exercice, examtype_slug: selectedExamType?.slug }"
          :lock-for-package="!havePackage(selectedExamType?.slug)"
          @start="startExercice"
        />
      </div>

      <!-- État vide -->
      <EmptyState
        v-if="!isLoading && !error && isEmpty && currentStep !== 3"
        :message="getEmptyMessage()"
        :showBackButton="currentStep > 1"
        @goBack="goBack"
      />
    </div>
  </div>

  <div class="w-full pb-8 container">
    <div
      class="grid mx-auto grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 bg-[#92d0f5] text-gray-600 rounded-xl py-4"
    >
      <div
        class="flex flex-col items-center justify-center p-4 border-b sm:border-b-0 sm:border-r border-white"
      >
        <span class="text-2xl font-bold">+10 000</span>
        <span class="text-sm text-gray-600 mt-1">Étudiants inscrits</span>
      </div>

      <div
        class="flex flex-col items-center justify-center p-4 border-b sm:border-b-0 lg:border-r border-white"
      >
        <span class="text-2xl font-bold">+100</span>
        <span class="text-sm text-gray-600 mt-1">Exercices pratiques</span>
      </div>

      <div
        class="flex flex-col items-center justify-center p-4 border-b sm:border-b-0 sm:border-r lg:border-r-0 border-white"
      >
        <span class="text-2xl font-bold">+95%</span>
        <span class="text-sm text-gray-600 mt-1">De réussite au TCF</span>
      </div>

      <div
        class="flex flex-col items-center justify-center p-4 sm:border-b-0 sm:border-l lg:border-r-0 border-white"
      >
        <span class="text-2xl font-bold">+3500</span>
        <span class="text-sm text-gray-600 mt-1">Questions</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useLanguageStore } from '@/stores/language'
import { useToast } from '@/composables/useToast'
import { useRouter } from 'vue-router'

import ExamTypeCard from '@/components/exercices/ExamTypeCard.vue'
import ExamCard from '@/components/exercices/ExamCard.vue'
import ExerciceCard from '@/components/exercices/ExerciceCard.vue'
import EmptyState from '@/components/exercices/EmptyState.vue'
import LoadingState from '@/components/exercices/LoadingState.vue'
import ErrorState from '@/components/exercices/ErrorState.vue'
import { useAuthStore } from '@/stores/auth.js'

const store = useLanguageStore()
const authStore = useAuthStore()

const { success, error: showError, warning } = useToast()
const router = useRouter()

// Reactive state
const currentStep = ref(1) // 1: exam types, 2: exams, 3: exercices
const selectedExam = ref(null)
const selectedExamType = ref(null)
const isLoading = ref(true)
const error = ref(null)
const user_subscriptions = ref([])
let unlocked_exercice_count = 0

// Computed properties from store
const exams = computed(() => store.exams)
const examTypes = computed(() => store.examTypes)
const exercices = computed(() => store.exercices)
const practiceType = computed(() => store.practiceType)
const isAuthenticated = computed(() => authStore.isAuthenticated)

const isEmpty = computed(() => {
  if (currentStep.value === 1) return examTypes.value.length === 0
  if (currentStep.value === 2) return exams.value.length === 0
  if (currentStep.value === 3) return exercices.value.length === 0
  return false
})

// Methods for titles and messages
const getTitle = () => {
  switch (currentStep.value) {
    case 1:
      return 'Choisissez un examen'
    case 2:
      return `Types d'exercices - ${selectedExamType.value?.name}`
    case 3:
      return `Mode de pratique`
    case 4:
      return `Exercices - ${selectedExam.value?.name}`
    default:
      return 'Exercices'
  }
}

const getSubtitle = () => {
  switch (currentStep.value) {
    case 1:
      return "Sélectionnez l'examen pour lequel vous souhaitez vous entraîner"
    case 2:
      return "Choisissez le type d'exercices qui vous intéresse"
    case 3:
      return 'Comment souhaitez-vous vous entraîner ?'
    case 4:
      return 'Sélectionnez un exercice pour commencer votre entraînement'
    default:
      return ''
  }
}

const getEmptyMessage = () => {
  switch (currentStep.value) {
    case 1:
      return 'Aucun examen disponible pour le moment'
    case 2:
      return 'Aucun type disponible pour cet examen'
    case 3:
      return 'Empty'
    case 4:
      return 'Aucun exercice disponible pour ce type'
    default:
      return 'Aucun élément trouvé'
  }
}

// Navigation and data loading methods
const selectExamType = async (examType) => {
  selectedExamType.value = examType
  currentStep.value = 2
  await loadExams()
}

const selectExam = async (exam) => {
  selectedExam.value = exam
  currentStep.value = 3
  // await loadExercices()
}

const havePackage = (exam_type_slug) => {
  if (user_subscriptions.value?.package?.exam_type === exam_type_slug) return true
  else {
    unlocked_exercice_count = unlocked_exercice_count + 1
    return unlocked_exercice_count <= 1
  }
}

const resetStepToTwo = () => {
  currentStep.value = 2
  selectedExam.value = null
}

const selectPracticeType = async (type) => {
  store.changePracticeType(type)
  await loadExercices()
  if (type === 'exam') {
    currentStep.value = 4
  } else {
    router.push({ name: 'exercice-training', params: { slug_exam: selectedExam.value?.slug } })
  }
  unlocked_exercice_count = 0
}

const startExercice = async (exercice) => {
  if (isAuthenticated.value) {
    try {
      isLoading.value = true
      const result = await store.startExercice(exercice.slug)

      if (result.success) {
        success('Exercice démarré avec succès!')
        router.push({ name: 'exercice', params: { slug: exercice.slug } })
      } else {
        showError(result.error || "Erreur lors du démarrage de l'exercice")
      }
    } catch (err) {
      showError("Erreur lors du démarrage de l'exercice")
      console.error(err)
    } finally {
      isLoading.value = false
    }
  } else {
    router.push({ name: 'login' })
  }
}

const goBack = () => {
  if (currentStep.value === 4) {
    currentStep.value = 3
    practiceType.value = null
  } else if (currentStep.value === 3) {
    currentStep.value = 2
    selectedExam.value = null // Retourne à la sélection des examens
  } else if (currentStep.value === 2) {
    currentStep.value = 1
    selectedExamType.value = null // Retourne à la sélection des types d'examens
  }
}

const loadExams = async () => {
  try {
    isLoading.value = true
    error.value = null
    await store.fetchExams()
  } catch (err) {
    error.value = 'Erreur lors du chargement des examens'
    showError(error.value)
    console.error(err)
  } finally {
    isLoading.value = false
  }
}

const loadExamTypes = async () => {
  try {
    isLoading.value = true
    error.value = null
    await store.fetchExamTypes()
  } catch (err) {
    error.value = "Erreur lors du chargement des types d'examen"
    showError(error.value)
    console.error(err)
  } finally {
    isLoading.value = false
  }
}

const loadExercices = async () => {
  try {
    isLoading.value = true
    error.value = null

    // This fetches exercises by exam slug, which is correct according to your API doc: /exercice/exam/{search_slug}/
    await store.fetchExercicesByExam(selectedExam.value.slug)
    if (exercices.value.length === 0) {
      // Check .value for reactive reference
      warning('Aucun exercice disponible pour cet examen')
    }
  } catch (err) {
    error.value = 'Erreur lors du chargement des exercices'
    showError(error.value)
    console.error(err)
  } finally {
    isLoading.value = false
  }
}

const retry = () => {
  error.value = null
  if (currentStep.value === 1) {
    loadExamTypes()
  } else if (currentStep.value === 2) {
    loadExams()
  } else if (currentStep.value === 3) {
    loadExercices()
  }
}

// Lifecycle hook
onMounted(async () => {
  const user_data = await authStore.fetchUser()
  user_subscriptions.value = user_data?.current_subscription
  await loadExamTypes()

  // Récupération des query params (messages uniquement)
  const urlParams = new URLSearchParams(window.location.search)
  const successMsg = urlParams.get('success')
  const errorMsg = urlParams.get('error')

  if (successMsg) success(successMsg)
  if (errorMsg) showError(errorMsg)

  // Récupération des params dynamiques (slugs)
  const { examTypeSlug, examSlug, practice } = router.currentRoute.value.params

  if (examTypeSlug) {
    await loadExamTypes()
    const data = examTypes.value.find((e) => e.slug === examTypeSlug)
    if (data) await selectExamType(data)
  }

  if (examSlug) {
    await loadExams()
    const data = exams.value.find((e) => e.slug === examSlug)
    if (data) {
      await selectExam(data)
      if (practice === 'exam') await selectPracticeType('exam')
    }
  }

  // Nettoyage des query (enlève success/error mais garde les autres)
  const { success, error, ...rest } = router.currentRoute.value.query

  await router.replace({
    path: router.currentRoute.value.path, // garde la route dynamique (avec slugs)
    query: rest, // conserve ce qui reste (si besoin)
  })
})
</script>
